import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, auth } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Plus, Clock, CheckCircle, Phone, Search, ShieldCheck, X, Star, Upload } from "lucide-react";
import { getServiceIcon } from "@/lib/serviceIcons";
import { TUTOR_SERVICE_ID, TUTOR_CLASSES, getSubjectsForClass, needsSubjects } from "@/lib/tutorConfig";
import ServiceExtraFields, { validateExtraFields } from "@/components/ServiceExtraFields";
import { getDescriptionHint } from "@/lib/serviceFormConfig";
import WhatsAppButton from "@/components/WhatsAppButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CustomerDashboard() {
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState(["All"]);
  const [activeCat, setActiveCat] = useState("All");
  const [q, setQ] = useState("");
  const [leads, setLeads] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    service_id: "",
    description: "",
    pincode: "",
    address: "",
    preferred_time: "",
    tutor_details: { student_name: "", cls: "", subjects: [], area_location: "" },
    extra_details: {},
    images: [],
  });
  const [uploading, setUploading] = useState(false);
  const [viewPartner, setViewPartner] = useState(null);
  const [loadingPartner, setLoadingPartner] = useState(false);
  const [reviewFor, setReviewFor] = useState(null); // lead being reviewed
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: "" });
  const user = auth.getUser();

  const openPartnerProfile = async (partnerId) => {
    setLoadingPartner(true);
    setViewPartner({ loading: true });
    try {
      const r = await api.get(`/customers/partners/${partnerId}`);
      setViewPartner(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load partner");
      setViewPartner(null);
    } finally {
      setLoadingPartner(false);
    }
  };

  const uploadImages = async (files) => {
    setUploading(true);
    const uploaded = [];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(r.data.filename);
      } catch {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    setUploading(false);
    setForm((f) => ({ ...f, images: [...f.images, ...uploaded] }));
  };

  const removeImage = (filename) => {
    setForm((f) => ({ ...f, images: f.images.filter((i) => i !== filename) }));
  };

  const markCompleted = async (leadId) => {
    if (!window.confirm("Mark this job as completed? You can then leave a review.")) return;
    try {
      await api.post(`/customers/leads/${leadId}/complete`);
      toast.success("Job marked completed");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!reviewFor) return;
    try {
      await api.post(`/customers/leads/${reviewFor.id}/review`, reviewForm);
      toast.success("Thanks for your review!");
      setReviewFor(null);
      setReviewForm({ rating: 5, comment: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const load = async () => {
    const [s, c, l] = await Promise.all([
      api.get("/services"),
      api.get("/service-categories"),
      api.get("/customers/leads"),
    ]);
    setServices(s.data);
    setCategories(c.data);
    setLeads(l.data);
  };

  useEffect(() => {
    load();
    if (user) {
      setForm((f) => ({
        ...f,
        contact_name: user.name || "",
        contact_mobile: user.mobile || "",
        address: user.address || "",
        location: user.location || "",
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return services.filter((s) => {
      const catOk = activeCat === "All" || s.category === activeCat;
      const qOk = !needle || s.name.toLowerCase().includes(needle) || (s.description || "").toLowerCase().includes(needle);
      return catOk && qOk;
    });
  }, [services, activeCat, q]);

  const createLead = async (e) => {
    e.preventDefault();
    if (!form.service_id || !form.description || !form.pincode || !form.address) {
      return toast.error("Please fill all required fields");
    }
    if (!form.contact_name.trim() || form.contact_mobile.length < 10) {
      return toast.error("Please provide contact name and a valid 10-digit mobile");
    }
    const isTutor = form.service_id === TUTOR_SERVICE_ID;
    if (isTutor) {
      const td = form.tutor_details;
      if (!td.student_name || !td.cls) return toast.error("Student name and class are required");
      if (needsSubjects(td.cls) && td.subjects.length === 0) return toast.error("Pick at least one subject");
    }
    const missing = validateExtraFields(form.service_id, form.extra_details);
    if (missing) return toast.error(`Please fill: ${missing}`);

    const payload = {
      service_id: form.service_id,
      contact_name: form.contact_name,
      contact_mobile: form.contact_mobile,
      description: form.description,
      pincode: form.pincode,
      address: form.address,
      location: form.location,
      preferred_time: form.preferred_time,
      tutor_details: isTutor ? form.tutor_details : null,
      extra_details: isTutor ? null : (Object.keys(form.extra_details).length ? form.extra_details : null),
      images: form.images,
    };
    try {
      await api.post("/customers/leads", payload);
      toast.success("Request submitted! Partners in your area will respond.");
      setShowCreate(false);
      setForm({
        service_id: "",
        contact_name: user?.name || "",
        contact_mobile: user?.mobile || "",
        description: "",
        pincode: "",
        address: user?.address || "",
        location: user?.location || "",
        preferred_time: "",
        tutor_details: { student_name: "", cls: "", subjects: [], area_location: "" },
        extra_details: {},
        images: [],
      });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create request");
    }
  };

  const choosePartner = async (leadId, partnerId) => {
    try {
      await api.post(`/customers/leads/${leadId}/choose/${partnerId}`);
      toast.success("Partner selected. They will contact you shortly.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Navbar variant="dashboard" />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#1E3F20] font-semibold">
              Customer Dashboard
            </div>
            <h1 className="font-display text-4xl mt-2">
              Hi {user?.name?.split(" ")[0] || "there"} 👋
            </h1>
            <p className="text-[#4A4A4A] mt-2">
              Book a service or track your existing requests.
            </p>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="h-12 px-6 rounded-full btn-accent font-semibold inline-flex items-center gap-2"
            data-testid="customer-new-request-btn"
          >
            <Plus className="w-4 h-4" /> New Request
          </button>
        </div>

        {/* Services strip */}
        <div className="mt-10">
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm font-semibold text-[#737373] uppercase tracking-widest">
              Browse services
            </div>
          </div>

          {/* Search + Categories */}
          <div className="mb-5 flex flex-col md:flex-row gap-3 md:items-center">
            <div className="relative md:w-72">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#737373]" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search services..."
                className="octa-input pl-11"
                data-testid="customer-services-search"
              />
            </div>
            <div className="flex-1 flex flex-wrap gap-2">
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setActiveCat(c)}
                  className={`px-3.5 h-9 rounded-full text-xs font-semibold transition-colors border ${
                    activeCat === c
                      ? "bg-[#1E3F20] text-white border-[#1E3F20]"
                      : "bg-white text-[#1A1A1A] border-[#E5E3DB] hover:border-[#1E3F20]"
                  }`}
                  data-testid={`customer-category-${c.replace(/\s|&/g, "-").toLowerCase()}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
            {filtered.length === 0 && (
              <div className="col-span-2 md:col-span-4 lg:col-span-8 text-center py-10 text-[#737373]" data-testid="customer-no-services">
                No services match your search.
              </div>
            )}
            {filtered.map((s) => {
              const Icon = getServiceIcon(s.icon);
              return (
                <button
                  key={s.id}
                  onClick={() => {
                    setForm({ ...form, service_id: s.id });
                    setShowCreate(true);
                  }}
                  className="card-octa p-4 text-left"
                  data-testid={`customer-service-${s.id}`}
                >
                  <div className="w-9 h-9 rounded-full bg-[#F3F1ED] flex items-center justify-center mb-3">
                    <Icon className="w-4 h-4 text-[#1E3F20]" />
                  </div>
                  <div className="font-display-medium text-sm">{s.name}</div>
                  <div className="text-xs text-[#737373] mt-1 line-clamp-1">
                    {s.description}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Leads */}
        <div className="mt-12">
          <div className="text-sm font-semibold text-[#737373] uppercase tracking-widest mb-4">
            My Requests
          </div>
          {leads.length === 0 ? (
            <div className="card-octa p-10 text-center" data-testid="customer-no-leads">
              <div className="font-display text-2xl">No requests yet</div>
              <p className="text-[#737373] mt-2 text-sm">
                Tap &ldquo;New Request&rdquo; to book your first service.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {leads.map((lead) => (
                <div
                  key={lead.id}
                  className="card-octa p-6"
                  data-testid={`customer-lead-${lead.id}`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="font-display-medium text-xl">
                        {lead.service_name}
                      </div>
                      <p className="text-sm text-[#4A4A4A] mt-1 max-w-xl">
                        {lead.description}
                      </p>
                      <div className="text-xs text-[#737373] mt-2">
                        Pincode {lead.pincode} · {new Date(lead.created_at).toLocaleString()}
                      </div>
                    </div>
                    <div>
                      {lead.status === "open" ? (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#F3F1ED] text-[#1A1A1A] text-xs font-semibold">
                          <Clock className="w-3 h-3" /> Open
                        </span>
                      ) : lead.status === "assigned" ? (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#1E3F20] text-white text-xs font-semibold">
                          <CheckCircle className="w-3 h-3" /> Assigned
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#E06D53] text-white text-xs font-semibold">
                          <CheckCircle className="w-3 h-3" /> Completed
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Images preview */}
                  {(lead.images || []).length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {lead.images.map((fname) => (
                        <a key={fname} href={`${BACKEND_URL}/api/files/${fname}`} target="_blank" rel="noreferrer" className="w-16 h-16 rounded-lg overflow-hidden border border-[#E5E3DB] block">
                          <img src={`${BACKEND_URL}/api/files/${fname}`} alt="" className="w-full h-full object-cover" />
                        </a>
                      ))}
                    </div>
                  )}

                  {/* Action: Mark completed (when assigned, not yet completed) */}
                  {lead.status === "assigned" && (
                    <div className="mt-4 flex flex-wrap items-center gap-3">
                      <button
                        onClick={() => markCompleted(lead.id)}
                        className="h-10 px-4 rounded-full btn-primary-octa text-xs font-semibold inline-flex items-center gap-2"
                        data-testid={`mark-completed-${lead.id}`}
                      >
                        <CheckCircle className="w-4 h-4" /> Mark job as completed
                      </button>
                    </div>
                  )}

                  {/* Action: Leave review (when completed and no review yet) */}
                  {lead.status === "completed" && !lead.review && (
                    <div className="mt-4">
                      <button
                        onClick={() => {
                          setReviewFor(lead);
                          setReviewForm({ rating: 5, comment: "" });
                        }}
                        className="h-10 px-4 rounded-full btn-accent text-xs font-semibold inline-flex items-center gap-2"
                        data-testid={`leave-review-${lead.id}`}
                      >
                        <Star className="w-4 h-4" /> Leave a review
                      </button>
                    </div>
                  )}

                  {/* Existing review */}
                  {lead.review && (
                    <div className="mt-4 p-4 rounded-xl bg-[#F3F1ED] border border-[#E5E3DB]" data-testid={`review-display-${lead.id}`}>
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <Star key={n} className={`w-4 h-4 ${n <= lead.review.rating ? "fill-[#E06D53] text-[#E06D53]" : "text-[#E5E3DB]"}`} />
                        ))}
                        <span className="ml-2 text-xs text-[#737373]">Your review</span>
                      </div>
                      {lead.review.comment && (
                        <p className="text-sm text-[#4A4A4A] mt-2">{lead.review.comment}</p>
                      )}
                    </div>
                  )}

                  {/* Responded partners */}
                  {lead.partner_details && lead.partner_details.length > 0 && (
                    <div className="mt-5 pt-5 border-t border-[#E5E3DB]">
                      <div className="text-xs uppercase tracking-widest text-[#737373] font-semibold mb-3">
                        {lead.partner_details.length} partner(s) responded
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {lead.partner_details.map((p) => {
                          const isChosen = lead.chosen_partner_id === p.id;
                          return (
                            <div
                              key={p.id}
                              className={`p-4 rounded-xl border ${isChosen ? "border-[#1E3F20] bg-[#1E3F20]/5" : "border-[#E5E3DB]"}`}
                              data-testid={`responded-partner-${p.id}`}
                            >
                              <div className="font-display-medium">{p.name}</div>
                              <div className="text-xs text-[#737373] mt-1">
                                Service area: {p.service_pincode}
                              </div>
                              <button
                                type="button"
                                onClick={() => openPartnerProfile(p.id)}
                                className="mt-2 text-xs font-semibold text-[#1E3F20] hover:text-[#E06D53] inline-flex items-center gap-1"
                                data-testid={`view-partner-${p.id}`}
                              >
                                View profile →
                              </button>
                              {isChosen ? (
                                <div className="mt-3 space-y-2">
                                  <div className="flex items-center gap-2 text-sm text-[#1E3F20] font-semibold">
                                    <Phone className="w-3.5 h-3.5" /> {p.mobile}
                                  </div>
                                  <WhatsAppButton
                                    phone={p.mobile}
                                    text={`Hi ${p.name?.split(" ")[0] || ""}, I picked you for my ${lead.service_name} request on OCTA Services. When can you come?`}
                                    label="WhatsApp partner"
                                    testId={`whatsapp-partner-${p.id}`}
                                  />
                                </div>
                              ) : (
                                <button
                                  onClick={() => choosePartner(lead.id, p.id)}
                                  disabled={!!lead.chosen_partner_id}
                                  className="mt-3 h-9 px-4 rounded-full btn-primary-octa text-xs font-semibold disabled:opacity-40"
                                  data-testid={`choose-partner-${p.id}`}
                                >
                                  Choose this partner
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* New request modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end md:items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-7 fade-in">
            <div className="flex items-center justify-between mb-5">
              <div className="font-display text-2xl">New Service Request</div>
              <button
                onClick={() => setShowCreate(false)}
                className="text-[#737373] hover:text-[#1A1A1A]"
                data-testid="modal-close-btn"
              >
                ✕
              </button>
            </div>
            <form onSubmit={createLead} className="space-y-4 max-h-[75vh] overflow-y-auto">
              <div>
                <label className="text-sm font-medium">Service</label>
                <select
                  className="octa-input mt-2"
                  value={form.service_id}
                  onChange={(e) => setForm({ ...form, service_id: e.target.value })}
                  data-testid="lead-service-select"
                >
                  <option value="">Choose a service</option>
                  {services.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>

              <div className="rounded-xl border border-[#E5E3DB] bg-[#F9F8F6] p-4 space-y-3">
                <div className="text-xs uppercase tracking-widest text-[#737373] font-semibold">
                  Contact for this booking
                </div>
                <p className="text-[11px] text-[#737373] -mt-1">
                  Pre-filled from your profile — edit if you are booking for someone else.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium">Name</label>
                    <input
                      className="octa-input mt-2"
                      value={form.contact_name}
                      onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                      data-testid="lead-contact-name"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Mobile</label>
                    <input
                      className="octa-input mt-2"
                      value={form.contact_mobile}
                      onChange={(e) => setForm({ ...form, contact_mobile: e.target.value.replace(/\D/g, "").slice(0, 10) })}
                      placeholder="10-digit"
                      data-testid="lead-contact-mobile"
                    />
                  </div>
                </div>
              </div>

              {form.service_id === TUTOR_SERVICE_ID && (
                <div className="rounded-xl border border-[#1E3F20]/30 bg-[#F3F1ED] p-4 space-y-4" data-testid="tutor-fields">
                  <div className="text-xs uppercase tracking-widest text-[#1E3F20] font-semibold">
                    Home Tutor — student details
                  </div>
                  <div>
                    <label className="text-sm font-medium">Student name</label>
                    <input
                      className="octa-input mt-2"
                      value={form.tutor_details.student_name}
                      onChange={(e) =>
                        setForm({ ...form, tutor_details: { ...form.tutor_details, student_name: e.target.value } })
                      }
                      placeholder="Student's full name"
                      data-testid="tutor-student-name"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Class</label>
                    <select
                      className="octa-input mt-2"
                      value={form.tutor_details.cls}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          tutor_details: { ...form.tutor_details, cls: e.target.value, subjects: [] },
                        })
                      }
                      data-testid="tutor-class-select"
                    >
                      <option value="">Choose class</option>
                      {TUTOR_CLASSES.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                  {needsSubjects(form.tutor_details.cls) && (
                    <div>
                      <label className="text-sm font-medium">Subjects (pick one or more)</label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                        {getSubjectsForClass(form.tutor_details.cls).map((sub) => {
                          const active = form.tutor_details.subjects.includes(sub);
                          return (
                            <button
                              key={sub}
                              type="button"
                              onClick={() =>
                                setForm({
                                  ...form,
                                  tutor_details: {
                                    ...form.tutor_details,
                                    subjects: active
                                      ? form.tutor_details.subjects.filter((s) => s !== sub)
                                      : [...form.tutor_details.subjects, sub],
                                  },
                                })
                              }
                              className={`text-xs font-medium p-2 rounded-lg border text-center transition-colors ${active ? "bg-[#1E3F20] text-white border-[#1E3F20]" : "bg-white border-[#E5E3DB] hover:border-[#1E3F20]"}`}
                              data-testid={`tutor-subject-${sub.replace(/\s|&/g, "-").toLowerCase()}`}
                            >
                              {sub}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium">Area / Locality</label>
                    <input
                      className="octa-input mt-2"
                      value={form.tutor_details.area_location}
                      onChange={(e) =>
                        setForm({ ...form, tutor_details: { ...form.tutor_details, area_location: e.target.value } })
                      }
                      placeholder="e.g. Sector 18, Noida"
                      data-testid="tutor-area-location"
                    />
                  </div>
                </div>
              )}

              {form.service_id && form.service_id !== TUTOR_SERVICE_ID && (
                <ServiceExtraFields
                  serviceId={form.service_id}
                  value={form.extra_details}
                  onChange={(v) => setForm({ ...form, extra_details: v })}
                />
              )}

              <div>
                <label className="text-sm font-medium">Describe your issue / requirement</label>
                <textarea
                  className="octa-input mt-2"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder={getDescriptionHint(form.service_id)}
                  data-testid="lead-description-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium">Pincode</label>
                  <input
                    className="octa-input mt-2"
                    value={form.pincode}
                    onChange={(e) => setForm({ ...form, pincode: e.target.value.replace(/\D/g, "").slice(0, 6) })}
                    placeholder="6-digit"
                    data-testid="lead-pincode-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Preferred time</label>
                  <input
                    className="octa-input mt-2"
                    value={form.preferred_time}
                    onChange={(e) => setForm({ ...form, preferred_time: e.target.value })}
                    placeholder="Today 5 pm"
                    data-testid="lead-time-input"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Location / Area</label>
                <input
                  className="octa-input mt-2"
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  placeholder="e.g. Sector 18, Noida"
                  data-testid="lead-location-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Address</label>
                <textarea
                  className="octa-input mt-2"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  data-testid="lead-address-input"
                />
              </div>

              <div>
                <label className="text-sm font-medium">Photos (optional)</label>
                <label className="mt-2 flex items-center gap-3 octa-input cursor-pointer">
                  <Upload className="w-4 h-4 text-[#737373]" />
                  <span className="text-sm text-[#737373]">
                    {uploading ? "Uploading..." : "Add photos of the issue (multiple)"}
                  </span>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => e.target.files && uploadImages(Array.from(e.target.files))}
                    data-testid="lead-images-input"
                  />
                </label>
                {form.images.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2" data-testid="lead-image-preview">
                    {form.images.map((fname) => (
                      <div key={fname} className="relative w-20 h-20 rounded-lg overflow-hidden border border-[#E5E3DB]">
                        <img src={`${BACKEND_URL}/api/files/${fname}`} alt="" className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => removeImage(fname)}
                          className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 text-white text-xs flex items-center justify-center"
                          data-testid={`remove-image-${fname}`}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <button
                type="submit"
                className="w-full h-12 rounded-full btn-accent font-semibold"
                data-testid="lead-submit-btn"
              >
                Submit Request
              </button>
            </form>
          </div>
        </div>
      )}
      {/* Review modal */}
      {reviewFor && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setReviewFor(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-7 fade-in" onClick={(e) => e.stopPropagation()} data-testid="review-modal">
            <div className="flex items-center justify-between mb-5">
              <div className="font-display text-2xl">Rate your experience</div>
              <button onClick={() => setReviewFor(null)} className="text-[#737373] hover:text-[#1A1A1A]">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-[#737373] mb-5">
              How was the <strong>{reviewFor.service_name}</strong> service?
            </p>
            <form onSubmit={submitReview} className="space-y-5">
              <div className="flex items-center justify-center gap-2">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setReviewForm({ ...reviewForm, rating: n })}
                    className="p-1"
                    data-testid={`review-star-${n}`}
                  >
                    <Star className={`w-9 h-9 transition-colors ${n <= reviewForm.rating ? "fill-[#E06D53] text-[#E06D53]" : "text-[#E5E3DB]"}`} />
                  </button>
                ))}
              </div>
              <textarea
                className="octa-input"
                placeholder="Tell us about your experience (optional)"
                value={reviewForm.comment}
                onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                data-testid="review-comment-input"
              />
              <button
                type="submit"
                className="w-full h-12 rounded-full btn-accent font-semibold"
                data-testid="review-submit-btn"
              >
                Submit review
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Partner Profile Preview modal */}
      {viewPartner && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setViewPartner(null)}>
          <div
            className="bg-white rounded-2xl w-full max-w-lg p-7 fade-in"
            onClick={(e) => e.stopPropagation()}
            data-testid="partner-profile-modal"
          >
            <div className="flex items-center justify-between mb-5">
              <div className="font-display text-2xl">Partner profile</div>
              <button
                onClick={() => setViewPartner(null)}
                className="text-[#737373] hover:text-[#1A1A1A]"
                data-testid="partner-profile-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {viewPartner.loading ? (
              <div className="text-sm text-[#737373] py-10 text-center">Loading...</div>
            ) : (
              <div className="space-y-5">
                <div>
                  <div className="font-display text-2xl">{viewPartner.name}</div>
                  <div className="text-sm text-[#737373] mt-1">
                    {viewPartner.service_location} · Pincode {viewPartner.service_pincode} · Radius {viewPartner.service_radius_km} km
                  </div>
                  {viewPartner.review_count > 0 ? (
                    <div className="mt-2 flex items-center gap-1 text-sm" data-testid="partner-profile-rating">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star key={n} className={`w-4 h-4 ${n <= Math.round(viewPartner.avg_rating) ? "fill-[#E06D53] text-[#E06D53]" : "text-[#E5E3DB]"}`} />
                      ))}
                      <span className="ml-1 text-[#1A1A1A] font-semibold">{viewPartner.avg_rating}</span>
                      <span className="text-[#737373]">({viewPartner.review_count} review{viewPartner.review_count === 1 ? "" : "s"})</span>
                    </div>
                  ) : (
                    <div className="mt-2 text-xs text-[#737373]">No reviews yet</div>
                  )}
                </div>

                <div>
                  <div className="text-xs uppercase tracking-widest text-[#737373] font-semibold mb-2">Services</div>
                  <div className="flex flex-wrap gap-1.5">
                    {(viewPartner.services || []).map((sid) => {
                      const s = services.find((x) => x.id === sid);
                      return (
                        <span key={sid} className="px-2.5 py-1 rounded-full bg-[#1E3F20] text-white text-xs font-semibold">
                          {s?.name || sid}
                        </span>
                      );
                    })}
                  </div>
                </div>

                {viewPartner.tutor_profile && (viewPartner.tutor_profile.classes?.length || viewPartner.tutor_profile.subjects?.length) ? (
                  <div>
                    <div className="text-xs uppercase tracking-widest text-[#737373] font-semibold mb-2">Tutor profile</div>
                    {viewPartner.tutor_profile.classes?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {viewPartner.tutor_profile.classes.map((c) => (
                          <span key={c} className="px-2 py-0.5 rounded-full bg-[#F3F1ED] border border-[#E5E3DB] text-xs font-medium">{c}</span>
                        ))}
                      </div>
                    )}
                    {viewPartner.tutor_profile.subjects?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {viewPartner.tutor_profile.subjects.map((s) => (
                          <span key={s} className="px-2 py-0.5 rounded-full bg-white border border-[#E5E3DB] text-xs">{s}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ) : null}

                <div>
                  <div className="text-xs uppercase tracking-widest text-[#737373] font-semibold mb-2">Verification</div>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { key: "aadhar", label: "Aadhar" },
                      { key: "pan", label: "PAN" },
                      { key: "gst", label: "GST" },
                      { key: "licence", label: "Trade Licence" },
                    ].map((k) => (
                      <span
                        key={k.key}
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${viewPartner.kyc?.[k.key] ? "bg-[#1E3F20] text-white" : "bg-[#F3F1ED] text-[#737373]"}`}
                      >
                        <ShieldCheck className="w-3 h-3" /> {k.label} {viewPartner.kyc?.[k.key] ? "verified" : "not submitted"}
                      </span>
                    ))}
                  </div>
                </div>

                {viewPartner.mobile && (
                  <div className="rounded-xl p-4 bg-[#F3F1ED] border border-[#E5E3DB] space-y-3">
                    <div>
                      <div className="text-xs uppercase tracking-widest text-[#1E3F20] font-semibold">Contact</div>
                      <a href={`tel:${viewPartner.mobile}`} className="mt-1 inline-flex items-center gap-2 font-display text-xl text-[#1E3F20]" data-testid="partner-profile-call">
                        <Phone className="w-4 h-4" /> {viewPartner.mobile}
                      </a>
                    </div>
                    <WhatsAppButton
                      phone={viewPartner.mobile}
                      text={`Hi ${viewPartner.name?.split(" ")[0] || ""}, I connected with you on OCTA Services.`}
                      label="Chat on WhatsApp"
                      testId="partner-profile-whatsapp"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
